import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { userStore } from '@/shared/store/userStore';

export const useChatStore = defineStore("chatStore", () => {
  // State
  const conversations = ref({
    messages: [],
    groups: [],
    marketplace: [],
  });

  // Full copies for filtering (to avoid losing data when filtering)
  const fullCopyOfConversations = ref({
    messages: [],
    groups: [],
    marketplace: [],
  });

  // Track conversations being added to prevent race conditions
  const conversationsBeingAdded = ref(new Set());
  
  // Track locally created conversations to ignore WebSocket events for a short time
  const locallyCreatedConversations = ref(new Set());

  // Pagination state
  const pagination = ref({
    messages: { currentPage: 1, totalPages: Infinity },
    groups: { currentPage: 1, totalPages: Infinity },
    marketplace: { currentPage: 1, totalPages: Infinity },
  });

  // Loading states
  const loading = ref({
    messages: false,
    groups: false,
    marketplace: false,
  });

  // Error states
  const errors = ref({
    messages: null,
    groups: null,
    marketplace: null,
  });

  // Active tab and conversation
  const activeTab = ref("messages");
  const activeConversationId = ref(null);

  // Search query
  const searchQuery = ref("");

  // Pending requests count
  const pendingRequestsCount = ref(0);

  // Friends list
  const friends = ref([]);

  // Getters
  const getConversationsByTab = computed(() => {
    return (tab) => {
      if (tab === "messages") {
        const result = conversations.value.messages;
        console.log(`[chatStore.getConversationsByTab] ${tab}: Returning ${result.length} messages`);
        return result;
      }
      if (tab === "groups") {
        const result = conversations.value.groups;
        console.log(`[chatStore.getConversationsByTab] ${tab}: Returning ${result.length} groups`);
        return result;
      }
      if (tab === "marketplace") {
        const result = conversations.value.marketplace;
        console.log(`[chatStore.getConversationsByTab] ${tab}: Returning ${result.length} marketplace conversations`, result);
        return result;
      }
      console.log(`[chatStore.getConversationsByTab] ${tab}: Unknown tab, returning empty array`);
      return [];
    };
  });

  const getActiveConversation = computed(() => {
    if (!activeConversationId.value) return null;

    // Search in messages first
    const messageConv = conversations.value.messages.find(
      (c) => c.id === activeConversationId.value
    );
    if (messageConv) return messageConv;

    // Search in groups
    const groupConv = conversations.value.groups.find(
      (c) => c.id === activeConversationId.value
    );
    if (groupConv) return groupConv;

    // Search in marketplace
    const marketplaceConv = conversations.value.marketplace.find(
      (c) => c.id === activeConversationId.value
    );
    if (marketplaceConv) return marketplaceConv;

    return null;
  });

  const isLoading = computed(() => {
    return (tab) => {
      if (tab === "messages") {
        return loading.value.messages;
      }
      if (tab === "groups") {
        return loading.value.groups;
      }
      if (tab === "marketplace") {
        return loading.value.marketplace;
      }
      return false;
    };
  });

  const hasError = computed(() => {
    return (tab) => {
      if (tab === "messages") {
        return errors.value.messages !== null;
      }
      if (tab === "groups") {
        return errors.value.groups !== null;
      }
      if (tab === "marketplace") {
        return errors.value.marketplace !== null;
      }
      return false;
    };
  });

  const friendsConversations = computed(() => conversations.value.messages || []);
  const groupsConversations = computed(() => conversations.value.groups || []);
  const marketplaceConversations = computed(() => conversations.value.marketplace || []);

  // Actions
  function setActiveTab(tab) {
    activeTab.value = tab;
  }

  function setActiveConversation(id) {
    activeConversationId.value = id;
  }

  function setSearchQuery(query) {
    searchQuery.value = query;
  }

  function setPendingRequestsCount(count) {
    pendingRequestsCount.value = count;
  }

  function setFriends(friendsList) {
    friends.value = friendsList;
  }

  function setConversations(tab, data, append = false) {
    // Ensure data is always an array
    const conversationsData = Array.isArray(data) ? data : [];
    
    console.log(`[chatStore.setConversations] Setting ${conversationsData.length} conversations for tab: ${tab}`, conversationsData);
    
    // Filter out MARKETPLACE conversations from messages/groups tabs
    // and filter out DIRECT/GROUP conversations from marketplace tab
    const filteredData = conversationsData.filter(conv => {
      if (tab === "marketplace") {
        // Only keep MARKETPLACE conversations
        return conv.type === 'MARKETPLACE';
      } else if (tab === "messages") {
        // Only keep DIRECT conversations (exclude MARKETPLACE and GROUP)
        return conv.type === 'DIRECT';
      } else if (tab === "groups") {
        // Only keep GROUP conversations
        return conv.type === 'GROUP';
      }
      return true;
    });
    
    console.log(`[chatStore.setConversations] Filtered ${filteredData.length} conversations (from ${conversationsData.length}) for tab: ${tab}`);
    
    if (tab === "messages") {
      if (append) {
        conversations.value.messages = Object.freeze([
          ...conversations.value.messages,
          ...filteredData,
        ]);
        fullCopyOfConversations.value.messages = [
          ...fullCopyOfConversations.value.messages,
          ...filteredData,
        ];
      } else {
        conversations.value.messages = Object.freeze(filteredData);
        fullCopyOfConversations.value.messages = [...filteredData];
      }
    } else if (tab === "groups") {
      if (append) {
        conversations.value.groups = Object.freeze([
          ...conversations.value.groups,
          ...filteredData,
        ]);
        fullCopyOfConversations.value.groups = [
          ...fullCopyOfConversations.value.groups,
          ...filteredData,
        ];
      } else {
        conversations.value.groups = Object.freeze(filteredData);
        fullCopyOfConversations.value.groups = [...filteredData];
      }
      console.log(`[chatStore.setConversations] Groups conversations after update:`, conversations.value.groups);
    } else if (tab === "marketplace") {
      if (append) {
        conversations.value.marketplace = Object.freeze([
          ...conversations.value.marketplace,
          ...filteredData,
        ]);
        fullCopyOfConversations.value.marketplace = [
          ...fullCopyOfConversations.value.marketplace,
          ...filteredData,
        ];
      } else {
        conversations.value.marketplace = Object.freeze(filteredData);
        fullCopyOfConversations.value.marketplace = [...filteredData];
      }
      console.log(`[chatStore.setConversations] Marketplace conversations after update:`, conversations.value.marketplace);
    }
  }

  function setPagination(tab, paginationData) {
    if (tab === "messages") {
      pagination.value.messages = {
        currentPage: paginationData.currentPage || 1,
        totalPages: paginationData.totalPages || Infinity,
      };
    } else if (tab === "groups") {
      pagination.value.groups = {
        currentPage: paginationData.currentPage || 1,
        totalPages: paginationData.totalPages || Infinity,
      };
    } else if (tab === "marketplace") {
      pagination.value.marketplace = {
        currentPage: paginationData.currentPage || 1,
        totalPages: paginationData.totalPages || Infinity,
      };
    }
  }

  function setLoading(tab, isLoading) {
    if (tab === "messages") {
      loading.value.messages = isLoading;
    } else if (tab === "groups") {
      loading.value.groups = isLoading;
    } else if (tab === "marketplace") {
      loading.value.marketplace = isLoading;
    }
  }

  function setError(tab, error) {
    if (tab === "messages") {
      errors.value.messages = error;
    } else if (tab === "groups") {
      errors.value.groups = error;
    } else if (tab === "marketplace") {
      errors.value.marketplace = error;
    }
  }

  function updateConversation(conversation) {
    // Update in messages (new array to allow freeze)
    const messageIndex = conversations.value.messages.findIndex(
      (c) => c.id === conversation.id
    );
    if (messageIndex !== -1) {
      const next = [...conversations.value.messages];
      next[messageIndex] = { ...next[messageIndex], ...conversation };
      conversations.value.messages = Object.freeze(next);
    }

    // Update in full copy
    const fullCopyIndex = fullCopyOfConversations.value.messages.findIndex(
      (c) => c.id === conversation.id
    );
    if (fullCopyIndex !== -1) {
      fullCopyOfConversations.value.messages[fullCopyIndex] = {
        ...fullCopyOfConversations.value.messages[fullCopyIndex],
        ...conversation,
      };
    }

    // Update in groups if it's a group conversation (new array to allow freeze)
    if (conversation.type === 'GROUP') {
      const groupIndex = conversations.value.groups.findIndex(
        (c) => c.id === conversation.id
      );
      if (groupIndex !== -1) {
        const next = [...conversations.value.groups];
        next[groupIndex] = { ...next[groupIndex], ...conversation };
        conversations.value.groups = Object.freeze(next);
      }

      const groupFullCopyIndex = fullCopyOfConversations.value.groups.findIndex(
        (c) => c.id === conversation.id
      );
      if (groupFullCopyIndex !== -1) {
        fullCopyOfConversations.value.groups[groupFullCopyIndex] = {
          ...fullCopyOfConversations.value.groups[groupFullCopyIndex],
          ...conversation,
        };
      }
    }

    // Update in marketplace if it's a marketplace conversation (new array to allow freeze)
    if (conversation.type === 'MARKETPLACE') {
      const marketplaceIndex = conversations.value.marketplace.findIndex(
        (c) => c.id === conversation.id
      );
      if (marketplaceIndex !== -1) {
        const next = [...conversations.value.marketplace];
        next[marketplaceIndex] = { ...next[marketplaceIndex], ...conversation };
        conversations.value.marketplace = Object.freeze(next);
      }

      const marketplaceFullCopyIndex =
        fullCopyOfConversations.value.marketplace.findIndex(
          (c) => c.id === conversation.id
        );
      if (marketplaceFullCopyIndex !== -1) {
        fullCopyOfConversations.value.marketplace[marketplaceFullCopyIndex] = {
          ...fullCopyOfConversations.value.marketplace[marketplaceFullCopyIndex],
          ...conversation,
        };
      }
    }
  }

  function addConversation(conversation) {
    if (!conversation || !conversation.id) {
      console.warn('[chatStore.addConversation] Invalid conversation:', conversation);
      return;
    }

    const conversationId = conversation.id;
    const conversationType = conversation.type || 'DIRECT';

    // Check if this conversation is currently being added (race condition protection)
    if (conversationsBeingAdded.value.has(conversationId)) {
      console.log('[chatStore.addConversation] Conversation already being added, skipping:', conversationId, {
        type: conversationType,
        stack: new Error().stack
      });
      return;
    }

    // Mark as being added FIRST to prevent race conditions
    if (conversationsBeingAdded.value.has(conversationId)) {
      console.log('[chatStore.addConversation] Conversation already being added (race condition), skipping:', conversationId);
      return;
    }
    conversationsBeingAdded.value.add(conversationId);
    
    // Mark as locally created IMMEDIATELY to ignore WebSocket events
    locallyCreatedConversations.value.add(conversationId);
    setTimeout(() => {
      locallyCreatedConversations.value.delete(conversationId);
    }, 2000);
    
    // Check if conversation already exists in any location
    const existsAnywhere = 
      conversations.value.messages.some(c => c.id === conversationId) ||
      conversations.value.groups.some(c => c.id === conversationId) ||
      conversations.value.marketplace.some(c => c.id === conversationId);
    
    if (existsAnywhere) {
      console.log('[chatStore.addConversation] Conversation already exists, skipping:', conversationId, {
        type: conversationType,
        inMessages: conversations.value.messages.some(c => c.id === conversationId),
        inGroups: conversations.value.groups.some(c => c.id === conversationId),
        inMarketplace: conversations.value.marketplace.some(c => c.id === conversationId)
      });
      // Clean up
      conversationsBeingAdded.value.delete(conversationId);
      return;
    }

    console.log('[chatStore.addConversation] Starting to add conversation:', conversationId, conversationType);

    try {
      // Handle MARKETPLACE conversations separately
      if (conversationType === 'MARKETPLACE') {
        const existsInMarketplace = conversations.value.marketplace.some(
          (c) => c.id === conversationId
        );
        if (existsInMarketplace) {
          console.log('[chatStore.addConversation] MARKETPLACE conversation already exists:', conversationId);
          return;
        }
        conversations.value.marketplace = Object.freeze([conversation, ...conversations.value.marketplace]);
        fullCopyOfConversations.value.marketplace = [conversation, ...fullCopyOfConversations.value.marketplace];
        sortConversations('marketplace');
        console.log('[chatStore.addConversation] Added MARKETPLACE conversation:', conversationId);
        return;
      }

      // Handle GROUP conversations separately
      if (conversationType === 'GROUP') {
        const beforeCount = conversations.value.groups.length;
        console.log('[chatStore.addConversation] GROUP - Before add, count:', beforeCount, 'conversationId:', conversationId);
        
        // Double-check before adding (race condition protection)
        const existsInGroups = conversations.value.groups.some(
          (c) => c.id === conversationId
        );
        
        if (existsInGroups) {
          console.log('[chatStore.addConversation] GROUP - BLOCKED, already in groups:', conversationId);
          conversationsBeingAdded.value.delete(conversationId);
          return;
        }
        
        console.log('[chatStore.addConversation] GROUP - Adding to groups array, conversationId:', conversationId);
        
        // Check one more time right before adding (final safety check)
        const finalCheck = conversations.value.groups.some(c => c.id === conversationId);
        if (finalCheck) {
          console.log('[chatStore.addConversation] GROUP - FINAL CHECK BLOCKED, already exists right before unshift:', conversationId);
          conversationsBeingAdded.value.delete(conversationId);
          return;
        }
        
        // Add to groups array ONCE - use a Set to prevent duplicates during the operation
        console.log('[chatStore.addConversation] GROUP - Executing unshift on groups array');
        
        // Use a more defensive approach: check and add atomically
        const currentGroups = [...conversations.value.groups];
        const alreadyExists = currentGroups.some(c => c.id === conversationId);
        
        if (alreadyExists) {
          console.error('[chatStore.addConversation] GROUP - CRITICAL! Conversation exists in array snapshot before unshift!', conversationId);
          conversationsBeingAdded.value.delete(conversationId);
          return;
        }
        
        // Add using a new array (freeze to avoid Vue deep tracking)
        conversations.value.groups = Object.freeze([conversation, ...currentGroups]);
        console.log('[chatStore.addConversation] GROUP - After adding with spread, count:', conversations.value.groups.length);
        
        // Verify immediately
        const verifyCount = conversations.value.groups.filter(c => c.id === conversationId).length;
        if (verifyCount > 1) {
          console.error('[chatStore.addConversation] GROUP - ERROR! Duplicate detected immediately after add! Count:', verifyCount);
          // Remove all duplicates, keep only one
          const seen = new Set();
          conversations.value.groups = Object.freeze(
            conversations.value.groups.filter(c => {
              if (c.id === conversationId) {
                if (seen.has(conversationId)) {
                  return false; // Remove duplicate
                }
                seen.add(conversationId);
                return true; // Keep first occurrence
              }
              return true;
            })
          );
          console.log('[chatStore.addConversation] GROUP - Removed duplicates, new count:', conversations.value.groups.length);
        }
        
        // Add to full copy using same defensive approach
        const currentFullCopy = [...fullCopyOfConversations.value.groups];
        const existsInFullCopy = currentFullCopy.some(c => c.id === conversationId);
        if (!existsInFullCopy) {
          fullCopyOfConversations.value.groups = [conversation, ...currentFullCopy];
        }
        
        const afterCount = conversations.value.groups.length;
        
        console.log('[chatStore.addConversation] GROUP - Added successfully:', conversationId, {
          beforeCount,
          afterCount,
          expectedIncrease: 1,
          actualIncrease: afterCount - beforeCount,
          groupsArrayLength: conversations.value.groups.length,
          fullCopyLength: fullCopyOfConversations.value.groups.length
        });
        
        sortConversations('groups');
        
        // Final verification
        const finalVerifyExists = conversations.value.groups.some(c => c.id === conversationId);
        const finalVerifyCount = conversations.value.groups.filter(c => c.id === conversationId).length;
        console.log('[chatStore.addConversation] GROUP - Final Verification:', {
          exists: finalVerifyExists,
          count: finalVerifyCount,
          shouldBeOne: finalVerifyCount === 1,
          conversationId,
          totalGroups: conversations.value.groups.length
        });
        
        if (finalVerifyCount > 1) {
          console.error('[chatStore.addConversation] GROUP - CRITICAL ERROR! Still have duplicates after cleanup!', {
            conversationId,
            count: finalVerifyCount,
            allIds: conversations.value.groups.map(c => c.id)
          });
        }
        
        return;
      }

      // For DIRECT conversations, add to messages list
      // Check in all possible locations to avoid duplicates
      const existsInMessages = conversations.value.messages.some(
        (c) => c.id === conversationId
      );
      const existsInGroups = conversations.value.groups.some(
        (c) => c.id === conversationId
      );
      const existsInMarketplace = conversations.value.marketplace.some(
        (c) => c.id === conversationId
      );
      
      if (existsInMessages || existsInGroups || existsInMarketplace) {
        console.log('[chatStore.addConversation] DIRECT conversation already exists:', conversationId, {
          inMessages: existsInMessages,
          inGroups: existsInGroups,
          inMarketplace: existsInMarketplace
        });
        return;
      }
      
      conversations.value.messages = Object.freeze([conversation, ...conversations.value.messages]);
      fullCopyOfConversations.value.messages = [conversation, ...fullCopyOfConversations.value.messages];
      sortConversations('messages');
      console.log('[chatStore.addConversation] Added DIRECT conversation:', conversationId);
    } finally {
      // Remove from being added set after a short delay to allow for async operations
      setTimeout(() => {
        conversationsBeingAdded.value.delete(conversationId);
      }, 100);
    }
  }

  function updateLastMessage(conversationId, message) {
    // Validation des paramètres
    if (!conversationId || !message) {
      console.warn('updateLastMessage: Missing conversationId or message', { conversationId, message });
      return;
    }
    
    // Determine which tab this conversation belongs to
    let conversationTab = null;
    
    // Get the message timestamp for sorting - prioriser updatedAt puis createdAt
    const messageTimestamp = message?.updatedAt || 
                            message?.createdAt || 
                            message?.lastMessageAt ||
                            new Date().toISOString();
    
    // Update in messages
    const messageConv = conversations.value.messages.find(
      (c) => c.id === conversationId
    );
    if (messageConv) {
      messageConv.lastMessage = message;
      messageConv.lastMessageAt = messageTimestamp;
      conversationTab = "messages";
    }

    const fullCopyConv = fullCopyOfConversations.value.messages.find(
      (c) => c.id === conversationId
    );
    if (fullCopyConv) {
      fullCopyConv.lastMessage = message;
      fullCopyConv.lastMessageAt = messageTimestamp;
    }

    // Update in groups
    const groupConv = conversations.value.groups.find(
      (c) => c.id === conversationId
    );
    if (groupConv) {
      groupConv.lastMessage = message;
      groupConv.lastMessageAt = messageTimestamp;
      conversationTab = "groups";
    }

    const groupFullCopyConv = fullCopyOfConversations.value.groups.find(
      (c) => c.id === conversationId
    );
    if (groupFullCopyConv) {
      groupFullCopyConv.lastMessage = message;
      groupFullCopyConv.lastMessageAt = messageTimestamp;
    }

    // Update in marketplace
    const marketplaceConv = conversations.value.marketplace.find(
      (c) => c.id === conversationId
    );
    if (marketplaceConv) {
      marketplaceConv.lastMessage = message;
      marketplaceConv.lastMessageAt = messageTimestamp;
      conversationTab = "marketplace";
    }

    const marketplaceFullCopyConv =
      fullCopyOfConversations.value.marketplace.find(
        (c) => c.id === conversationId
      );
    if (marketplaceFullCopyConv) {
      marketplaceFullCopyConv.lastMessage = message;
      marketplaceFullCopyConv.lastMessageAt = messageTimestamp;
    }

    // Sort conversations to move the updated conversation to the top
    if (conversationTab) {
      sortConversations(conversationTab);
    }
  }

  function deleteConversation(conversationId) {
    conversations.value.messages = Object.freeze(
      conversations.value.messages.filter((c) => c.id !== conversationId)
    );
    fullCopyOfConversations.value.messages =
      fullCopyOfConversations.value.messages.filter(
        (c) => c.id !== conversationId
      );
    conversations.value.groups = Object.freeze(
      conversations.value.groups.filter((c) => c.id !== conversationId)
    );
    fullCopyOfConversations.value.groups =
      fullCopyOfConversations.value.groups.filter(
        (c) => c.id !== conversationId
      );
    conversations.value.marketplace = Object.freeze(
      conversations.value.marketplace.filter((c) => c.id !== conversationId)
    );
    fullCopyOfConversations.value.marketplace =
      fullCopyOfConversations.value.marketplace.filter(
        (c) => c.id !== conversationId
      );
  }

  // Cache for last sort timestamp to avoid unnecessary sorting
  const lastSortTimestamp = ref({
    messages: null,
    groups: null,
    marketplace: null,
  });

  function sortConversations(tab) {
    // Only sort if conversations have changed (optimization)
    const now = Date.now();
    
    // Skip sorting if done recently (within 100ms) to avoid excessive sorting
    if (lastSortTimestamp.value[tab] && (now - lastSortTimestamp.value[tab]) < 100) {
      return;
    }

    const sortFn = (a, b) => {
      const timeA = a.lastMessage?.updatedAt || a.lastMessageAt;
      const timeB = b.lastMessage?.updatedAt || b.lastMessageAt;

      if (!timeA && !timeB) return 0;
      if (!timeA) return 1;
      if (!timeB) return -1;

      return new Date(timeB) - new Date(timeA);
    };

    if (tab === "messages") {
      conversations.value.messages = Object.freeze([...conversations.value.messages].sort(sortFn));
      fullCopyOfConversations.value.messages = [...fullCopyOfConversations.value.messages].sort(sortFn);
      lastSortTimestamp.value.messages = now;
    } else if (tab === "groups") {
      conversations.value.groups = Object.freeze([...conversations.value.groups].sort(sortFn));
      fullCopyOfConversations.value.groups = [...fullCopyOfConversations.value.groups].sort(sortFn);
      lastSortTimestamp.value.groups = now;
    } else if (tab === "marketplace") {
      conversations.value.marketplace = Object.freeze([...conversations.value.marketplace].sort(sortFn));
      fullCopyOfConversations.value.marketplace = [...fullCopyOfConversations.value.marketplace].sort(sortFn);
      lastSortTimestamp.value.marketplace = now;
    }
  }

  function applyMuteState(conversationsArray) {
    if (!Array.isArray(conversationsArray)) return;

    const loggedUser = userStore().getUser();
    conversationsArray.forEach((conversation) => {
      const me = conversation?.participants?.find(
        (participant) =>
          (participant.user?.id ?? participant.userId) === loggedUser.id
      );
      conversation.isMuted = !!me?.isMuted;
    });
  }

  function filterConversations(tab, searchTerm) {
    console.log(`[chatStore.filterConversations] Tab: ${tab}, Search: "${searchTerm}"`);
    console.log(`[chatStore.filterConversations] Full copy - messages: ${fullCopyOfConversations.value.messages.length}, marketplace: ${fullCopyOfConversations.value.marketplace.length}`);
    
    if (!searchTerm || searchTerm.trim() === "") {
      // Reset to full copy when search is cleared
      if (tab === "messages") {
        conversations.value.messages = Object.freeze([
          ...fullCopyOfConversations.value.messages,
        ]);
        console.log(`[chatStore.filterConversations] Reset messages to ${conversations.value.messages.length} conversations`);
      } else if (tab === "groups") {
        conversations.value.groups = Object.freeze([
          ...fullCopyOfConversations.value.groups,
        ]);
        console.log(`[chatStore.filterConversations] Reset groups to ${conversations.value.groups.length} conversations`);
      } else if (tab === "marketplace") {
        conversations.value.marketplace = Object.freeze([
          ...fullCopyOfConversations.value.marketplace,
        ]);
        console.log(`[chatStore.filterConversations] Reset marketplace to ${conversations.value.marketplace.length} conversations`);
      }
      return;
    }

    const loggedUser = userStore().getUser();
    if (!loggedUser) {
      console.warn('[chatStore.filterConversations] No logged user found');
      return;
    }
    
    const searchLower = searchTerm.toLowerCase();

    if (tab === "messages") {
      conversations.value.messages = Object.freeze(
        fullCopyOfConversations.value.messages.filter((chat) => {
          const user = chat.participants?.find(
            (p) => (p.user?.id || p.userId) !== loggedUser.id
          )?.user;

          if (user) {
            const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
            return fullName.includes(searchLower);
          }
          return false;
        })
      );
    } else if (tab === "groups") {
      conversations.value.groups = Object.freeze(
        fullCopyOfConversations.value.groups.filter((chat) => {
          const groupName = (chat.name || '').toLowerCase();
          return groupName.includes(searchLower);
        })
      );
      console.log(`[chatStore.filterConversations] Filtered groups to ${conversations.value.groups.length} conversations`);
    } else if (tab === "marketplace") {
      conversations.value.marketplace = Object.freeze(
        fullCopyOfConversations.value.marketplace.filter((chat) => {
          const user = chat.participants?.find(
            (p) => (p.user?.id || p.userId) !== loggedUser.id
          )?.user;

          if (user) {
            const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
            return fullName.includes(searchLower);
          }
          return false;
        })
      );
      console.log(`[chatStore.filterConversations] Filtered marketplace to ${conversations.value.marketplace.length} conversations`);
    }
  }

  function updateUserStatusInConversations(userId, isLoggedIn) {
    const updateStatus = (chatList) => {
      chatList.forEach((chat) => {
        const participant = chat.participants?.find(
          (p) => (p.user?.id || p.userId) === userId
        );
        if (participant && participant.user) {
          participant.user.isLoggedIn = isLoggedIn;
        }
      });
    };

    updateStatus(conversations.value.messages);
    updateStatus(conversations.value.groups);
    updateStatus(conversations.value.marketplace);
    updateStatus(fullCopyOfConversations.value.messages);
    updateStatus(fullCopyOfConversations.value.groups);
    updateStatus(fullCopyOfConversations.value.marketplace);
  }

  function reset() {
    conversations.value = {
      messages: [],
      groups: [],
      marketplace: [],
    };
    fullCopyOfConversations.value = {
      messages: [],
      groups: [],
      marketplace: [],
    };
    pagination.value = {
      messages: { currentPage: 1, totalPages: Infinity },
      groups: { currentPage: 1, totalPages: Infinity },
      marketplace: { currentPage: 1, totalPages: Infinity },
    };
    loading.value = {
      messages: false,
      groups: false,
      marketplace: false,
    };
    errors.value = {
      messages: null,
      groups: null,
      marketplace: null,
    };
    activeTab.value = "messages";
    activeConversationId.value = null;
    searchQuery.value = "";
    pendingRequestsCount.value = 0;
    friends.value = [];
  }

  return {
    // State
    conversations,
    fullCopyOfConversations,
    pagination,
    loading,
    errors,
    activeTab,
    activeConversationId,
    searchQuery,
    pendingRequestsCount,
    friends,
    locallyCreatedConversations,

    // Getters
    getConversationsByTab,
    getActiveConversation,
    isLoading,
    hasError,
    friendsConversations,
    groupsConversations,
    marketplaceConversations,

    // Actions
    setActiveTab,
    setActiveConversation,
    setSearchQuery,
    setPendingRequestsCount,
    setFriends,
    setConversations,
    setPagination,
    setLoading,
    setError,
    updateConversation,
    addConversation,
    updateLastMessage,
    deleteConversation,
    sortConversations,
    applyMuteState,
    filterConversations,
    updateUserStatusInConversations,
    reset,
  };
});

